import { CognitoUser } from 'amazon-cognito-identity-js';
import { cookieStorage } from '.';

interface DeviceType {
  DeviceKey?: string;
}

function getCurrentDeviceKey(username: string, appClient: string): string | null {
  return cookieStorage.getItem(`CognitoIdentityServiceProvider.${appClient}.${username}.deviceKey`);
}

function forgetDevice(user: CognitoUser, deviceKey: string): Promise<boolean> {
  return new Promise(resolve => {
    user?.forgetSpecificDevice(deviceKey, {
      onSuccess: () => resolve(true),
      onFailure: (_err: Error) => resolve(false),
    });
  });
}

function dontRememberCurrentDevice(user: CognitoUser): Promise<boolean> {
  return new Promise(resolve => {
    user?.setDeviceStatusNotRemembered({
      onSuccess: () => resolve(true),
      onFailure: (_err: Error) => resolve(false),
    });
  });
}

async function clearListOfDevices(
  user: CognitoUser,
  devices: DeviceType[],
  username: string,
  appClient: string,
): Promise<boolean> {
  const currentDeviceKey = getCurrentDeviceKey(username, appClient);

  const results = await Promise.all(
    devices.filter(Boolean).flatMap(device => {
      const deviceKey = device?.DeviceKey;
      if (!deviceKey) return false;
      // we keep a record of the current device, but set its status to 'not remembered'
      // this prevents a logout on the current device, but ensures it won't be remembered for MFA purposes.
      if (deviceKey === currentDeviceKey) return dontRememberCurrentDevice(user);
      // we clear non current devices from the device list
      return forgetDevice(user, deviceKey);
    }),
  );

  return results.every(Boolean);
}

function listDevicesPage(
  user: CognitoUser,
  paginationToken: string | null,
): Promise<{ Devices?: DeviceType[]; PaginationToken?: string }> {
  return new Promise((resolve, reject) => {
    user?.listDevices(60, paginationToken, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export async function clearListOfDevicesAsync(
  user: CognitoUser,
  username: string,
  appClient: string,
): Promise<boolean> {
  try {
    let paginationToken: string | null = null;
    let allSucceeded = true;
    do {
      const result = await listDevicesPage(user, paginationToken);
      const pageSucceeded = await clearListOfDevices(user, result?.Devices ?? [], username, appClient);
      if (!pageSucceeded) allSucceeded = false;
      paginationToken = result?.PaginationToken ?? null;
    } while (paginationToken);
    return allSucceeded;
  } catch {
    return false;
  }
}
